import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip, Collapse, Skeleton,
  TextField, InputAdornment, Paper, Divider, Button, Card,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { fetchRecords } from '../services/api';

const fmt = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const fmtCurrency = (val, cur = 'INR') => {
  const num = Number(val);
  if (!Number.isFinite(num)) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(num);
};

export default function ElementEntryGrouped({ entries, onEdit, onDelete, onAdd, onView }) {
  const [expandedPeriods, setExpandedPeriods] = useState({});
  const [expandedAssignments, setExpandedAssignments] = useState({});
  const [search, setSearch] = useState('');
  
  const [assignments, setAssignments] = useState([]);
  const [elements, setElements] = useState([]);
  const [persons, setPersons] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [runs, setRuns] = useState([]);
  const [elementEntries, setElementEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('ELEMENT_TYPE').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAY_PERIOD').catch(() => ({ data: { data: [] } })),
      fetchRecords('PAYROLL_RUN').catch(() => ({ data: { data: [] } })),
      fetchRecords('ELEMENT_ENTRY').catch(() => ({ data: { data: [] } })),
    ]).then(([a, e, p, pr, r, ee]) => {
      setAssignments(a.data.data || []);
      setElements(e.data.data || []);
      setPersons(p.data.data || []);
      setPeriods(pr.data.data || []);
      setRuns(r.data.data || []);
      setElementEntries(ee.data.data || []);
      setLoading(false);
    });
  }, []);

  const assignmentMap = useMemo(() =>
    Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const elementMap = useMemo(() =>
    Object.fromEntries(elements.map(e => [e.element_id, e])), [elements]);
  const personMap = useMemo(() =>
    Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);
  const periodMap = useMemo(() =>
    Object.fromEntries(periods.map(p => [p.period_id, p])), [periods]);
  const runMap = useMemo(() =>
    Object.fromEntries(runs.map(r => [r.period_id, r])), [runs]); 
  const elementEntryMap = useMemo(() =>
    Object.fromEntries(elementEntries.map(ee => [ee.entry_id, ee])), [elementEntries]);

  const enrichedEntries = useMemo(() => {
    return entries.map(e => ({
      ...e,
      element_id: e.element_id || elementEntryMap[e.entry_id]?.element_id,
      pay_value: e.pay_value !== undefined && e.pay_value !== null ? e.pay_value : e.screen_entry_value,
      unit_of_measure: e.unit_of_measure || elementEntryMap[e.entry_id]?.unit_of_measure
    }));
  }, [entries, elementEntryMap]);

  // Initialize expanded state for periods (default expanded)
  useEffect(() => {
    if (!loading && entries.length > 0) {
      setExpandedPeriods(prev => {
        if (Object.keys(prev).length > 0) return prev; // Preserve if already set
        const initialExp = {};
        const uniquePeriods = [...new Set(entries.map(e => e.period_id || 'ONGOING'))];
        uniquePeriods.forEach(p => {
          initialExp[p] = true;
        });
        return initialExp;
      });
    }
  }, [loading, entries]);

  const togglePeriod = (periodId) => {
    setExpandedPeriods(prev => ({
      ...prev,
      [periodId]: !prev[periodId]
    }));
  };

  const toggleAssignment = (periodId, asnKey) => {
    setExpandedAssignments(prev => ({
      ...prev,
      [`${periodId}_${asnKey}`]: !prev[`${periodId}_${asnKey}`]
    }));
  };

  // Grouping logic: Period -> Assignment
  const groups = useMemo(() => {
    const pGroups = {};

    for (const e of enrichedEntries) {
      const periodId = e.period_id || 'ONGOING';
      
      if (!pGroups[periodId]) {
        const periodObj = periodId !== 'ONGOING' ? periodMap[periodId] : null;
        const runObj = periodId !== 'ONGOING' ? runMap[periodId] : null;
        
        pGroups[periodId] = {
          period_id: periodId,
          periodObj,
          runObj,
          periodLabel: periodId === 'ONGOING' ? 'Ongoing / No Period Assigned' : (periodObj?.period_name || periodId),
          sortDate: periodId === 'ONGOING' ? '0000-00-00' : (periodObj?.payment_date || periodObj?.start_date || '9999-99-99'),
          total_entries: 0,
          gross: 0,
          net: 0,
          activeEmployees: new Set(),
          assignments: {}
        };
      }
      
      const pGroup = pGroups[periodId];
      pGroup.total_entries++;
      
      const asnId = e.assignment_id || '—';
      pGroup.activeEmployees.add(asnId);
      
      if (!pGroup.assignments[asnId]) {
        const asn = assignmentMap[asnId];
        const person = asn ? personMap[asn.person_id] : null;
        const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;
        
        pGroup.assignments[asnId] = {
          assignment_id: asnId,
          assignment: asn,
          person,
          fullName: fullName || (asn ? asn.employee_code : 'Unknown employee'),
          total_earnings: 0,
          total_ded: 0,
          entries: []
        };
      }
      
      const asnGroup = pGroup.assignments[asnId];
      asnGroup.entries.push(e);
      
      const el = elementMap[e.element_id];
      const val = Number(e.pay_value) || 0;
      const cls = (el?.type || e.classification || '').toLowerCase();
      
      const isEarning = cls === 'earning' || cls.includes('earning') || cls.includes('allowance');
      const isDeduction = cls === 'deduction' || cls.includes('deduction') || cls.includes('tax');
      
      if (isEarning) {
        asnGroup.total_earnings += val;
        pGroup.gross += val;
      } else if (isDeduction) {
        asnGroup.total_ded += val;
      }
    }
    
    // Sort assignments and calculate net
    return Object.values(pGroups)
      .map(p => {
        p.net = p.gross - Object.values(p.assignments).reduce((sum, a) => sum + a.total_ded, 0);
        
        const sortedAssignments = Object.values(p.assignments).sort((a, b) => a.fullName.localeCompare(b.fullName));
        sortedAssignments.forEach(a => {
          // Sort entries by entry_id ascending
          a.entries.sort((ea, eb) => (ea.entry_id || '').localeCompare(eb.entry_id || '', undefined, { numeric: true }));
        });
        
        p.assignments = sortedAssignments;
        return p;
      })
      .sort((a, b) => a.sortDate.localeCompare(b.sortDate));
  }, [enrichedEntries, assignmentMap, personMap, elementMap, periodMap, runMap]);

  // Filtering
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(p => {
        const periodHit = p.periodLabel.toLowerCase().includes(q) || p.period_id.toLowerCase().includes(q);
        
        if (periodHit) return p;
        
        const matchingAssignments = p.assignments.map(a => {
          const asnHits = 
            a.assignment_id.toLowerCase().includes(q) ||
            (a.assignment?.employee_code || '').toLowerCase().includes(q) ||
            (a.fullName || '').toLowerCase().includes(q);
            
          if (asnHits) return a;
          
          const matchingEntries = a.entries.filter(e => {
            const el = elementMap[e.element_id];
            return (
              (e.entry_id || '').toLowerCase().includes(q) ||
              (e.element_id || '').toLowerCase().includes(q) ||
              (el?.element_name || '').toLowerCase().includes(q) ||
              (el?.element_code || '').toLowerCase().includes(q)
            );
          });
          
          return matchingEntries.length ? { ...a, entries: matchingEntries } : null;
        }).filter(Boolean);
        
        return matchingAssignments.length ? { ...p, assignments: matchingAssignments } : null;
      })
      .filter(Boolean);
  }, [groups, search, elementMap]);

  const totalEntries = filtered.reduce((s, p) => s + p.total_entries, 0);

  if (loading) {
    return (
      <Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={60} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 2, gap: 2, flexWrap: 'wrap',
      }}>
        <TextField
          size="small"
          placeholder="Search by assignment, employee, or element…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{
            width: { xs: '100%', sm: 360 },
            '& .MuiOutlinedInput-root': {
              bgcolor: 'white', fontSize: 13, borderRadius: '10px',
              '& fieldset': { borderColor: '#E8EDF5', borderWidth: '1.5px' },
              '&:hover fieldset': { borderColor: '#bbf7d0' },
              '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: '2px' },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Stack direction="row" alignItems="center" gap={0.6}
            sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#10B981' }} />
            <Typography fontSize={12} fontWeight={600} color="text.secondary">
              {filtered.length} Periods
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.6}
            sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
            <Typography fontSize={12} fontWeight={600} color="text.secondary">
              {totalEntries} Entries
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* Grouped Rows */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {filtered.length === 0 ? (
          <Paper sx={{ py: 8, textAlign: 'center', border: '1.5px solid #E5E7EB', borderRadius: '12px' }} elevation={0}>
            <InboxIcon sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
            <Typography fontWeight={600} color="#374151">{search ? 'No matches found' : 'No entries available'}</Typography>
          </Paper>
        ) : (
          filtered.map(pGroup => {
            const isPeriodExpanded = expandedPeriods[pGroup.period_id];
            
            return (
              <Box key={pGroup.period_id} sx={{ 
                bgcolor: 'white', borderRadius: '12px', border: '1.5px solid #E5E7EB',
                boxShadow: isPeriodExpanded ? '0 4px 12px rgba(0,0,0,0.03)' : 'none',
                overflow: 'hidden', transition: 'all 0.2s'
              }}>
                <Box
                  onClick={() => togglePeriod(pGroup.period_id)}
                  sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                    px: 3, py: 2, cursor: 'pointer',
                    bgcolor: isPeriodExpanded ? '#F8FAFC' : 'white',
                    borderBottom: isPeriodExpanded ? '1px solid #E5E7EB' : 'none',
                    '&:hover': { bgcolor: '#F1F5F9' },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: '#EFF6FF', color: '#3B82F6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />
                  </Box>
                  
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Typography fontSize={18} fontWeight={700} color="#0F172A">{pGroup.periodLabel}</Typography>
                      {pGroup.period_id !== 'ONGOING' && (
                        <Chip label="Payroll Month" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
                      )}
                    </Stack>
                    <Typography fontSize={12} color="#64748B" mt={0.5}>
                      Element entries for {pGroup.periodLabel}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <Typography fontSize={10} color="#64748B" fontWeight={600} textTransform="uppercase">Staff</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#0F172A">{pGroup.activeEmployees.size} Active</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#FEFCE8', borderRadius: '8px', border: '1px solid #FEF08A' }}>
                      <Typography fontSize={10} color="#EAB308" fontWeight={600} textTransform="uppercase">Entries</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#A16207">{pGroup.total_entries}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F0FDF4', borderRadius: '8px', border: '1px solid #DCFCE7' }}>
                      <Typography fontSize={10} color="#16A34A" fontWeight={600} textTransform="uppercase">Earnings</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#15803D">{fmtCurrency(pGroup.gross)}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FEE2E2' }}>
                      <Typography fontSize={10} color="#EF4444" fontWeight={600} textTransform="uppercase">Deductions</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#B91C1C">{fmtCurrency(pGroup.gross - pGroup.net)}</Typography>
                    </Box>
                  </Box>

                  <IconButton size="small" sx={{ ml: 1, color: '#3B82F6' }}>
                    {isPeriodExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>

                <Collapse in={isPeriodExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    
                    {onAdd && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => onAdd()}
                          sx={{
                            fontSize: 12, py: 0.4, px: 1.4,
                            color: '#16a34a', borderColor: '#22c55e',
                            border: '1.5px solid #22c55e', borderRadius: '8px',
                            bgcolor: 'white',
                            '&:hover': { bgcolor: '#f0fdf4', borderColor: '#16a34a' },
                          }}
                        >
                          Add Entry
                        </Button>
                      </Box>
                    )}

                    <Stack spacing={2}>
                      {pGroup.assignments.map(asnGroup => {
                        const isAsnExpanded = expandedAssignments[`${pGroup.period_id}_${asnGroup.assignment_id}`];
                        
                        return (
                          <Card key={asnGroup.assignment_id} elevation={0} sx={{
                            border: '1.5px solid', borderColor: isAsnExpanded ? '#8B5CF6' : '#E2E8F0',
                            borderRadius: '12px', transition: 'all 0.2s',
                            boxShadow: isAsnExpanded ? '0 4px 12px rgba(139,92,246,0.08)' : '0 1px 2px rgba(0,0,0,0.02)'
                          }}>
                            {/* Assignment Header */}
                            <Box
                              onClick={() => toggleAssignment(pGroup.period_id, asnGroup.assignment_id)}
                              sx={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                                px: 2.5, py: 2, cursor: 'pointer',
                                bgcolor: 'white', borderRadius: '12px',
                                '&:hover': { bgcolor: '#F8FAFC' }
                              }}
                            >
                              <Box sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                background: 'linear-gradient(135deg,#8B5CF6,#6366F1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                <Typography fontWeight={700} fontSize={14} color="white">
                                  {asnGroup.fullName.substring(0, 2).toUpperCase()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Stack direction="row" alignItems="center" gap={1.5} mb={0.5}>
                                  <Typography fontSize={15} fontWeight={700} color="#0F172A">{asnGroup.fullName}</Typography>
                                  <Chip label={asnGroup.assignment_id} size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
                                </Stack>
                              </Box>
                              
                              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography fontSize={11} color="#64748B" fontWeight={600} mb={0.2} textTransform="uppercase">Net Earnings</Typography>
                                  <Typography fontSize={14} fontWeight={700} color="#2563EB">{fmtCurrency(asnGroup.total_earnings - asnGroup.total_ded)}</Typography>
                                </Box>
                                <IconButton size="small" sx={{ color: '#8B5CF6' }}>
                                  {isAsnExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </Box>
                            </Box>
                            
                            {/* Assignment Entries */}
                            <Collapse in={isAsnExpanded} unmountOnExit>
                              <Divider />
                              <Box sx={{ px: 2.5, pb: 2.5, pt: 1, bgcolor: 'white' }}>
                                <Box sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'minmax(90px,1fr) minmax(150px,1.5fr) minmax(120px,1fr) minmax(100px,1fr) minmax(120px,1fr) 100px',
                                  gap: 2, px: 2, py: 1.5,
                                  bgcolor: '#F1F5F9', borderRadius: '8px', mb: 1
                                }}>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Entry Id</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Element</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Effective From</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569">Classification</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569" textAlign="right">Amount</Typography>
                                  <Typography fontSize={12} fontWeight={700} color="#475569" textAlign="center">Actions</Typography>
                                </Box>
                                
                                {asnGroup.entries.map((entry, idx) => {
                                  const el = elementMap[entry.element_id] || {};
                                  const elName = el.element_name || entry.element_id || '—';
                                  const cls = (el.type || entry.classification || '').toLowerCase();
                                  
                                  return (
                                    <Box key={entry.entry_id || idx} sx={{
                                      display: 'grid',
                                      gridTemplateColumns: 'minmax(90px,1fr) minmax(150px,1.5fr) minmax(120px,1fr) minmax(100px,1fr) minmax(120px,1fr) 100px',
                                      gap: 2, px: 2, py: 1.5,
                                      borderBottom: idx < asnGroup.entries.length - 1 ? '1px solid #F1F5F9' : 'none',
                                      alignItems: 'center',
                                      '&:hover': { bgcolor: '#F8FAFC' }
                                    }}>
                                      <Typography fontSize={12} color="#64748B" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                                        {entry.entry_id || '—'}
                                      </Typography>
                                      
                                      <Box>
                                        <Typography fontSize={13} color="#334155" fontWeight={600}>{elName}</Typography>
                                        <Typography fontSize={11} color="#94A3B8" sx={{ fontFamily: 'monospace' }}>
                                          {entry.element_id || '—'}
                                        </Typography>
                                      </Box>
                                      
                                      <Typography fontSize={13} color="#64748B">{entry.effective_from || '—'}</Typography>
                                      
                                      <Box>
                                        {cls.includes('earning') || cls.includes('allowance') ? (
                                          <Chip label={el.type || entry.classification || 'Earning'} size="small" sx={{ height: 22, fontSize: 11, bgcolor: '#DCFCE7', color: '#16A34A', fontWeight: 600 }} />
                                        ) : cls.includes('deduction') || cls.includes('tax') ? (
                                          <Chip label={el.type || entry.classification || 'Deduction'} size="small" sx={{ height: 22, fontSize: 11, bgcolor: '#FEE2E2', color: '#DC2626', fontWeight: 600 }} />
                                        ) : (
                                          <Typography fontSize={13} color="#64748B">{el.type || entry.classification || '—'}</Typography>
                                        )}
                                      </Box>
                                      
                                      <Typography fontSize={13} fontWeight={600} color={
                                        cls.includes('earning') || cls.includes('allowance') ? '#15803D' : 
                                        cls.includes('deduction') || cls.includes('tax') ? '#B91C1C' : '#0F172A'
                                      } textAlign="right">
                                        {entry.unit_of_measure === 'Percentage' ? `${fmt(entry.pay_value)}%` : fmtCurrency(entry.pay_value, entry.currency || 'INR')}
                                      </Typography>
                                      
                                      <Stack direction="row" spacing={0.5} justifyContent="center">
                                        {onView && (
                                          <Tooltip title="View" arrow placement="top">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(entry); }} sx={{ color: '#3B82F6', '&:hover': { bgcolor: '#EFF6FF' } }}>
                                              <VisibilityIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {onEdit && (
                                          <Tooltip title="Edit" arrow placement="top">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(entry); }} sx={{ color: '#8B5CF6', '&:hover': { bgcolor: '#F5F3FF' } }}>
                                              <EditIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                        {onDelete && (
                                          <Tooltip title="Delete" arrow placement="top">
                                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(entry); }} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEF2F2' } }}>
                                              <DeleteIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                          </Tooltip>
                                        )}
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
